import { useRef, type ReactNode } from 'react';
import { useDialogA11y } from './useDialogA11y';

/**
 * A right-side slide-over drawer (UX-DR11 "quick-peek" pattern). Adapted from
 * SettingsPanel's overlay mechanics: a fixed scrim closes on click, Esc closes,
 * the panel receives focus on open, focus is restored to the opener on close,
 * and Tab is trapped within the dialog (all via the shared `useDialogA11y`
 * hook — Story 4.4). Unlike SettingsPanel this is right-aligned + full-height (a
 * drawer, not a centred modal). Drawers are quick-peeks only — full detail views
 * are full-page (see VendorDirectory's profile pane).
 */
export function Drawer({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}): React.JSX.Element {
  const panelRef = useRef<HTMLElement>(null);
  useDialogA11y(panelRef, onClose);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--scrim)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 'var(--z-modal)' as unknown as number,
      }}
    >
      <section
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="card stack"
        style={{
          width: 'min(420px, 100%)',
          height: '100%',
          borderRadius: 0,
          overflowY: 'auto',
          boxShadow: 'var(--elevation-modal)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{title}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        {children}
      </section>
    </div>
  );
}
